'use client';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import playbackControlModel from './PlaybackControlModel';
import {
  PiMusicNoteSimpleThin,
  PiPauseFill,
  PiPlayFill,
  PiSkipBackThin,
} from 'react-icons/pi';
import './PlaybackControl.css';
import { IconButton } from '../ui/button';
import { useManagerEntity } from '@/shared/managerHelpers';
import { PlayerState } from '@/sharedTypes/main';
import { TooltipRedNotes } from '../Tooltips/TooltipRedNotes';
import { TooltipPlaybackControl } from '../Tooltips/TooltipPlaybackControl';

export const PlaybackControl = observer(
  ({ mode }: { mode: 'Play' | 'Practice' }) => {
    const ManagerEntity = useManagerEntity();

    const playerState = ManagerEntity?.player?.state;

    useEffect(() => {
      if (mode === 'Practice' && playerState === PlayerState.PLAYING) {
        playbackControlModel.pause();
      }
    }, [mode, playerState]);

    if (!ManagerEntity) {
      return null;
    }

    const StartPlayButton = () => (
      <TooltipPlaybackControl action='play'>
        <IconButton
          data-testid='start_play_btn'
          onClick={() => {
            playbackControlModel.startPlay(); // Ensure this method is invoked
            console.log('**StartPlayButton clicked.', {
              playerState,
            });
          }}
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <PiPlayFill size={36} />
        </IconButton>
      </TooltipPlaybackControl>
    );

    const PauseButton = () => (
      <TooltipPlaybackControl action='pause'>
        <IconButton
          data-testid='pause_btn'
          onClick={() => {
            playbackControlModel.pause();
            console.log('**PauseButton clicked.', {
              playerState,
            });
          }}
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <PiPauseFill size={36} />
        </IconButton>
      </TooltipPlaybackControl>
    );

    const RewindButton = () => (
      <TooltipPlaybackControl action='rewind'>
        <IconButton
          data-testid='rewind_btn'
          onClick={() => {
            playbackControlModel.rewind();
            console.log('**RewindButton clicked.', {
              playerState,
            });
          }}
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <PiSkipBackThin size={36} />
        </IconButton>
      </TooltipPlaybackControl>
    );

    const RedNotesShowButton = () => {
      const selectedPracticeMode = ManagerEntity.player?.selectedPracticeMode;
      const notesVisible = selectedPracticeMode?.newNotesHandler?.notesVisible;

      return (
        <TooltipRedNotes notesVisible={!!notesVisible}>
          <IconButton
            data-testid='rednotes_show_btn'
            onClick={() => {
              playbackControlModel.triggerRedNotes();
              console.log('**RedNotesShowButton clicked.', {
                playerState,
              });
            }}
          >
            <PiMusicNoteSimpleThin
              size={36}
              className={notesVisible ? 'text-destructive' : 'text-black'}
            />
          </IconButton>
        </TooltipRedNotes>
      );
    };

    if (playerState === undefined) {
      return <div></div>;
    }
    return (
      <div className='flex items-center justify-center'>
        {mode === 'Practice' ? (
          <>
            <RewindButton />
            <RedNotesShowButton />
          </>
        ) : (
          <>
            {(playerState === PlayerState.ENDED ||
              playerState === PlayerState.PAUSE) && <RewindButton />}
            {playerState === PlayerState.PLAYING ? (
              <PauseButton />
            ) : (
              <StartPlayButton />
            )}
            <RedNotesShowButton />
          </>
        )}
      </div>
    );
  }
);
